import { Button, Group, Loader, Modal, MultiSelect, Stack, TextInput } from "@mantine/core";
import {
  fieldStyles,
  modalStyles,
  MODAL_MAX_WIDTH,
  multiSelectStyles,
  primaryBtnStyles,
  secondaryBtnStyles,
  useIsMobile,
} from "./formStyles";
import { useEffect, useState } from "react";
import { FiChevronLeft, FiEdit2, FiPlus, FiTrash2, FiUsers } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import styled from "styled-components";
import { useAuth } from "../../hooks/auth";
import { UnitService } from "../../services/unit/unit.service";
import { Teacher, UserService } from "../../services/user/user.service";
import { PageHeader, PageWrapper } from "./styles";

type UnitRow = {
  id: string;
  name: string;
  teachers?: { id: string; name: string }[];
};

const notify = (msg: string): void => {
  toast.warning(msg, { position: toast.POSITION.TOP_RIGHT });
};
const notifyOk = (msg: string): void => {
  toast.success(msg, { position: toast.POSITION.TOP_RIGHT });
};

export function Unidades() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const [units, setUnits] = useState<UnitRow[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<UnitRow | null>(null);
  const [name, setName] = useState("");
  const [teacherIds, setTeacherIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const fetch = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const [unitsRes, teachersRes] = await Promise.all([
        UnitService.get(),
        UserService.getTeachers(),
      ]);
      setUnits(unitsRes as UnitRow[]);
      setTeachers(teachersRes);
    } catch (error: any) {
      notify(error[0]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setTeacherIds([]);
    setModalOpen(true);
  };

  const openEdit = (unit: UnitRow) => {
    setEditing(unit);
    setName(unit.name);
    setTeacherIds((unit.teachers ?? []).map((t) => t.id));
    setModalOpen(true);
  };

  const handleSave = async (): Promise<void> => {
    if (!name.trim()) return notify("Informe o nome da unidade");
    if (teacherIds.length === 0)
      return notify("Selecione ao menos um professor");

    setIsSaving(true);
    try {
      if (editing) {
        await UnitService.update(editing.id, { name, teacher_ids: teacherIds });
        notifyOk("Unidade atualizada");
      } else {
        await UnitService.create({
          church_id: user.church_id,
          name,
          teacher_ids: teacherIds,
        });
        notifyOk("Unidade cadastrada");
      }
      setModalOpen(false);
      await fetch();
    } catch (error: any) {
      notify(error[0]);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (unit: UnitRow): Promise<void> => {
    if (!window.confirm(`Excluir a unidade "${unit.name}"?`)) return;
    try {
      await UnitService.remove(unit.id);
      notifyOk("Unidade excluída");
      await fetch();
    } catch (error: any) {
      notify(error[0]);
    }
  };

  return (
    <PageWrapper>
      <PageHeader onClick={() => navigate(-1)}>
        <FiChevronLeft />
        <span>Unidades</span>
      </PageHeader>

      <Content>
        <Toolbar>
          <PrimaryButton onClick={openCreate}>
            <FiPlus />
            <span>Nova unidade</span>
          </PrimaryButton>
        </Toolbar>

        <Panel>
          {isLoading ? (
            <Centered>
              <Loader color="#3439a5" />
            </Centered>
          ) : units.length === 0 ? (
            <Empty>Nenhuma unidade cadastrada.</Empty>
          ) : (
            <TableWrapper>
              <table>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Professores</th>
                    <th style={{ textAlign: "right" }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {units.map((unit) => (
                    <tr key={unit.id}>
                      <td>{unit.name}</td>
                      <td>
                        {(unit.teachers ?? []).map((t) => t.name).join(", ") ||
                          "—"}
                      </td>
                      <td>
                        <Actions>
                          <IconButton
                            title="Alunos"
                            onClick={() =>
                              navigate(`/unidades/${unit.id}/alunos`, {
                                state: { unitName: unit.name },
                              })
                            }
                          >
                            <FiUsers />
                          </IconButton>
                          <IconButton
                            title="Editar"
                            onClick={() => openEdit(unit)}
                          >
                            <FiEdit2 />
                          </IconButton>
                          <IconButton
                            danger
                            title="Excluir"
                            onClick={() => handleDelete(unit)}
                          >
                            <FiTrash2 />
                          </IconButton>
                        </Actions>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrapper>
          )}
        </Panel>
      </Content>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? "Editar unidade" : "Nova unidade"}
        centered
        radius="lg"
        size={MODAL_MAX_WIDTH}
        fullScreen={isMobile}
        overlayProps={{ opacity: 0.55, blur: 3 }}
        transitionProps={{ transition: "pop" }}
        styles={modalStyles}
      >
        <Stack spacing="lg">
          <TextInput
            size="md"
            styles={fieldStyles}
            label="Nome da unidade"
            placeholder="Ex: Unidade 1"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            withAsterisk
          />
          <MultiSelect
            size="md"
            styles={multiSelectStyles}
            label="Professores"
            placeholder="Selecione os professores"
            data={teachers.map((t) => ({ value: t.id, label: t.name }))}
            value={teacherIds}
            onChange={setTeacherIds}
            searchable
            withinPortal
            withAsterisk
            nothingFound="Nenhum professor encontrado"
          />
          <Group position="right" grow={isMobile} spacing="sm" mt="xs">
            <Button
              variant="default"
              styles={secondaryBtnStyles}
              onClick={() => setModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              color="indigo"
              styles={primaryBtnStyles}
              loading={isSaving}
              onClick={handleSave}
            >
              {editing ? "Salvar alterações" : "Cadastrar"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </PageWrapper>
  );
}

const Content = styled.div`
  flex: 1;
  padding: 1.6rem;
  max-width: 1100px;
  width: 100%;
  margin: 0 auto;

  @media (min-width: 768px) {
    padding: 2.4rem 3.2rem;
  }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1.6rem;
`;

const Panel = styled.div`
  background: #fff;
  border: 1px solid #c0c0c9;
  border-radius: 1rem;
  padding: 0.8rem;
  overflow: hidden;
`;

const TableWrapper = styled.div`
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th,
  td {
    font-size: 1.5rem;
    white-space: nowrap;
    padding: 1.4rem 1.6rem;
    text-align: left;
    border-bottom: 1px solid #eef0f4;
  }

  th {
    font-size: 1.3rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #7a7a80;
    font-weight: 700;
    background: #f7f8fb;
  }

  tbody tr {
    transition: background 0.12s;
  }
  tbody tr:hover {
    background: #f5f6fc;
  }
  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding: 3.2rem;
`;

const Empty = styled.div`
  padding: 3.2rem;
  text-align: center;
  color: #7a7a80;
  font-size: 1.4rem;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.8rem;
  justify-content: flex-end;
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  height: 3.6rem;
  padding: 0 1.4rem;
  border-radius: 0.6rem;
  background-color: #3439a5;
  border: 0.1rem solid #2b2fa3;
  color: #fff;
  font-size: 1.4rem;
  font-weight: bold;
  cursor: pointer;

  svg {
    font-size: 1.8rem;
  }
`;

const IconButton = styled.button<{ danger?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 0.6rem;
  border: 1px solid ${({ danger }) => (danger ? "#f1b0b7" : "#c0c0c9")};
  background: ${({ danger }) => (danger ? "#fdecea" : "#fff")};
  color: ${({ danger }) => (danger ? "#dc3545" : "#3439a5")};
  cursor: pointer;
  transition: filter 0.15s;

  svg {
    font-size: 1.6rem;
  }

  &:hover {
    filter: brightness(0.95);
  }
`;
